package er.prateek.crossly.ui.theme

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color

@Composable
fun AppTheme(
    themeState: ThemeState,
    content: @Composable () -> Unit,
) {
    val isDark = when (themeState.mode.value) {
        ThemeMode.SYSTEM -> isSystemInDarkTheme()
        ThemeMode.LIGHT -> false
        ThemeMode.DARK -> true
    }
    val target = paletteFor(themeState.paletteId.value).colors(isDark)

    // Switching palette or light/dark cross-fades the backdrop and accents rather
    // than hard-cutting, so changing the theme in Settings reads as part of the game.
    val colors = target.animated()

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val windowSize = remember(maxWidth, maxHeight) { WindowSize(maxWidth, maxHeight) }
        CompositionLocalProvider(
            LocalGameColors provides colors,
            LocalWindowSize provides windowSize,
        ) {
            MaterialTheme(
                colorScheme = colors.toColorScheme(isDark),
                typography = AppTypography,
                shapes = AppShapes,
                content = content,
            )
        }
    }
}

/** The game palette in scope. Shorthand for [LocalGameColors]. */
val MaterialTheme.game: GameColors
    @Composable @ReadOnlyComposable get() = LocalGameColors.current

/** Convenience for the responsive breakpoints in scope. */
val MaterialTheme.window: WindowSize
    @Composable @ReadOnlyComposable get() = LocalWindowSize.current

private const val ThemeFadeMillis = 420

@Composable
private fun GameColors.animated(): GameColors {
    // Only the tokens a player actually watches change are animated; cell status
    // tints and tier colors switch instantly (they are never on screen mid-swap).
    @Composable
    fun fade(color: Color, label: String) =
        animateColorAsState(color, tween(ThemeFadeMillis), label = label).value

    return GameColors(
        backdropTop = fade(backdropTop, "backdropTop"),
        backdropBottom = fade(backdropBottom, "backdropBottom"),
        glowA = fade(glowA, "glowA"),
        glowB = fade(glowB, "glowB"),
        gridTexture = fade(gridTexture, "gridTexture"),
        card = fade(card, "card"),
        cardElevated = fade(cardElevated, "cardElevated"),
        cardBorder = fade(cardBorder, "cardBorder"),
        shadow = fade(shadow, "shadow"),
        accent = fade(accent, "accent"),
        accentAlt = fade(accentAlt, "accentAlt"),
        onAccent = fade(onAccent, "onAccent"),
        accentSoft = fade(accentSoft, "accentSoft"),
        ink = fade(ink, "ink"),
        inkMuted = fade(inkMuted, "inkMuted"),
        gold = gold,
        goldEmpty = fade(goldEmpty, "goldEmpty"),
        flame = flame,
        success = success,
        danger = danger,
        locked = fade(locked, "locked"),
        cellFill = fade(cellFill, "cellFill"),
        cellBorder = fade(cellBorder, "cellBorder"),
        cellText = fade(cellText, "cellText"),
        cellNumber = fade(cellNumber, "cellNumber"),
        cellBlock = fade(cellBlock, "cellBlock"),
        cellWord = fade(cellWord, "cellWord"),
        cellFocus = fade(cellFocus, "cellFocus"),
        cellCorrect = cellCorrect,
        cellIncorrect = cellIncorrect,
        cellRevealed = cellRevealed,
        cellGiven = fade(cellGiven, "cellGiven"),
        tierEasy = tierEasy,
        tierMedium = tierMedium,
        tierHard = tierHard,
        tierExpert = tierExpert,
    )
}
